/**
 * 兑换码管理API服务
 */

const API_BASE_URL = "/api";

/**
 * 兑换码类型定义
 */
export interface RedemptionCode {
  id: string;
  oneapiId: number;
  code: string;
  name: string;
  quota: number;
  status: "active" | "disabled" | "used";
  count?: number;
  createdBy: string;
  redeemedBy?: string;
  createdAt: string;
  redeemedAt?: string;
  oneapiStatus?: number;
  metadata?: Record<string, any>;
}

/**
 * 兑换码列表响应
 */
export interface RedemptionCodeListResponse {
  success: boolean;
  data: {
    codes: RedemptionCode[];
    pagination: {
      page: number;
      page_size: number;
      total: number;
      total_pages: number;
    };
  };
  message: string;
}

/**
 * 兑换码统计响应
 */
export interface RedemptionCodeStatsResponse {
  success: boolean;
  data: {
    stats: {
      total: number;
      active: number;
      used: number;
      disabled: number;
    };
  };
  message: string;
}

/**
 * 创建兑换码请求
 */
export interface CreateRedemptionCodeRequest {
  name: string;
  quota: number;
  count: number;
}

/**
 * 兑换码状态配置
 */
export const REDEMPTION_CODE_STATUS = {
  active: { code: "active", name: "可用", color: "green" },
  disabled: { code: "disabled", name: "已禁用", color: "red" },
  used: { code: "used", name: "已使用", color: "gray" },
} as const;

export type RedemptionCodeStatusKey = keyof typeof REDEMPTION_CODE_STATUS;

/**
 * 获取认证token
 */
function getAuthHeaders(): HeadersInit {
  const authStore = localStorage.getItem("auth-storage");
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }
  }
  return {
    "Content-Type": "application/json",
  };
}

/**
 * 获取兑换码列表
 */
export async function getRedemptionCodeList(params: {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: string;
}): Promise<RedemptionCodeListResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.pageSize)
    queryParams.append("pageSize", params.pageSize.toString());
  if (params.keyword) queryParams.append("keyword", params.keyword);
  if (params.status) queryParams.append("status", params.status);

  const response = await fetch(
    `${API_BASE_URL}/admin/redemption-codes?${queryParams}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "获取兑换码列表失败");
  }

  return response.json();
}

/**
 * 批量创建兑换码
 */
export async function createRedemptionCodes(data: CreateRedemptionCodeRequest) {
  const response = await fetch(`${API_BASE_URL}/admin/redemption-codes/batch`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "创建兑换码失败");
  }

  return response.json();
}

/**
 * 更新兑换码状态
 */
export async function updateRedemptionCodeStatus(
  codeId: string,
  status: "active" | "disabled"
) {
  const response = await fetch(
    `${API_BASE_URL}/admin/redemption-codes/${codeId}/status`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "更新兑换码状态失败");
  }

  return response.json();
}

/**
 * 删除兑换码
 */
export async function deleteRedemptionCode(codeId: string) {
  const response = await fetch(
    `${API_BASE_URL}/admin/redemption-codes/${codeId}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "删除兑换码失败");
  }

  return response.json();
}

/**
 * 获取兑换码统计
 */
export async function getRedemptionCodeStats(): Promise<RedemptionCodeStatsResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/redemption-codes/stats`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "获取兑换码统计失败");
  }

  return response.json();
}

/**
 * 获取兑换码状态显示信息
 */
export function getRedemptionCodeStatusInfo(status: string | undefined) {
  if (!status) return REDEMPTION_CODE_STATUS.active;
  return (
    REDEMPTION_CODE_STATUS[status as RedemptionCodeStatusKey] ||
    REDEMPTION_CODE_STATUS.active
  );
}

/**
 * 格式化配额显示
 */
export function formatQuota(quota: number): string {
  return quota.toLocaleString();
}

/**
 * 格式化日期显示
 */
export function formatDate(dateString: string | undefined): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("zh-CN");
}
